/** Landing page access states — keep in sync with LandingPage status cards. */
export const LANDING_ACCESS = {
  UNDER_REVIEW: "under_review",
  ACCOUNT_NOT_FOUND: "account_not_found",
  SESSION_EXPIRED: "session_expired",
  CONNECTION_ERROR: "connection_error",
  NOT_LOGGED_IN: "not_logged_in",
};

function readErrorPayload(err) {
  const data = err?.response?.data;
  const status = err?.response?.status;
  const code = data?.code || null;
  const message = String(data?.error || data?.message || err?.message || "").toLowerCase();
  return { status, code, message, data };
}

export function isNetworkOrServerFailure(err) {
  const { status, message } = readErrorPayload(err);
  if (status >= 500) return true;
  if (
    !err?.response ||
    err?.code === "ERR_NETWORK" ||
    err?.code === "ECONNABORTED" ||
    message.includes("timeout") ||
    message.includes("network")
  ) {
    return true;
  }
  return false;
}

export function isSessionAuthFailure(err) {
  const { status, code, message } = readErrorPayload(err);
  if (status === 401) return true;
  if (
    message.includes("invalid token") ||
    message.includes("no token") ||
    message.includes("token expired") ||
    message.includes("jwt expired") ||
    code === "invalid_token"
  ) {
    return true;
  }
  return false;
}

/**
 * Classify a failed user fetch.
 * Valid auth + no users row = pending club approval, not "account not found".
 */
export function getFetchFailureInfo(result, { hasAuthSession = false } = {}) {
  if (!result || result.status === "fulfilled") return null;
  const err = result.reason;
  const { status, code, data } = readErrorPayload(err);

  if (isSessionAuthFailure(err)) {
    return { type: LANDING_ACCESS.SESSION_EXPIRED };
  }

  if (isNetworkOrServerFailure(err)) {
    return { type: LANDING_ACCESS.CONNECTION_ERROR };
  }

  if (code === "account_archived_or_missing" && status === 404) {
    return {
      type: LANDING_ACCESS.ACCOUNT_NOT_FOUND,
      archived: true,
    };
  }

  if (hasAuthSession) {
    if (code === "registration_pending_review") {
      return { type: LANDING_ACCESS.UNDER_REVIEW, pending: data?.pending || null };
    }
    if (status === 403 || status === 404 || code === "club_membership_required") {
      return { type: LANDING_ACCESS.UNDER_REVIEW };
    }
  }

  if (status === 404) {
    return { type: LANDING_ACCESS.ACCOUNT_NOT_FOUND, archived: code === "account_archived_or_missing" };
  }

  if (status >= 400 && status < 500) {
    return hasAuthSession
      ? { type: LANDING_ACCESS.UNDER_REVIEW }
      : { type: LANDING_ACCESS.ACCOUNT_NOT_FOUND };
  }

  return { type: LANDING_ACCESS.CONNECTION_ERROR };
}

/**
 * Resolve landing access when member fetch did not return a profile.
 * Rule: valid auth + no users data = waiting for club approval.
 */
export function resolveAccessBlockAfterFetch({
  mainFailure,
  childFailure,
  mergedUsersLength,
  hasAuthSession,
}) {
  if (
    mainFailure?.type === LANDING_ACCESS.SESSION_EXPIRED ||
    childFailure?.type === LANDING_ACCESS.SESSION_EXPIRED
  ) {
    return { type: LANDING_ACCESS.SESSION_EXPIRED };
  }

  // Network/server failures take priority over "auth but no profile" inference
  if (
    mainFailure?.type === LANDING_ACCESS.CONNECTION_ERROR ||
    childFailure?.type === LANDING_ACCESS.CONNECTION_ERROR
  ) {
    return { type: LANDING_ACCESS.CONNECTION_ERROR };
  }

  const archived = Boolean(mainFailure?.archived || childFailure?.archived);

  if (hasAuthSession && mergedUsersLength === 0) {
    if (archived) {
      return { type: LANDING_ACCESS.ACCOUNT_NOT_FOUND, archived: true };
    }
    return { type: LANDING_ACCESS.UNDER_REVIEW };
  }

  if (
    mainFailure?.type === LANDING_ACCESS.UNDER_REVIEW ||
    childFailure?.type === LANDING_ACCESS.UNDER_REVIEW
  ) {
    return { type: LANDING_ACCESS.UNDER_REVIEW };
  }

  if (
    mainFailure?.type === LANDING_ACCESS.ACCOUNT_NOT_FOUND ||
    childFailure?.type === LANDING_ACCESS.ACCOUNT_NOT_FOUND
  ) {
    return {
      type: LANDING_ACCESS.ACCOUNT_NOT_FOUND,
      archived,
    };
  }

  return null;
}

export function resolveAccessBlockFromLoadError(error, { hasAuthSession = false } = {}) {
  if (isSessionAuthFailure(error)) {
    return { type: LANDING_ACCESS.SESSION_EXPIRED };
  }
  if (isNetworkOrServerFailure(error)) {
    return { type: LANDING_ACCESS.CONNECTION_ERROR };
  }
  if (hasAuthSession) {
    const code = error?.response?.data?.code;
    if (code === "account_archived_or_missing") {
      return { type: LANDING_ACCESS.ACCOUNT_NOT_FOUND, archived: true };
    }
    return { type: LANDING_ACCESS.UNDER_REVIEW };
  }
  if (error?.response?.status === 404) {
    return {
      type: LANDING_ACCESS.ACCOUNT_NOT_FOUND,
      archived: error?.response?.data?.code === "account_archived_or_missing",
    };
  }
  return { type: LANDING_ACCESS.CONNECTION_ERROR };
}
